import type { SparTranscriptEntry } from '../spar/types';
import type { TranscriptSource, TranscriptSpeaker, TranscriptUtterance } from './types';

/**
 * Bridge between the Spar `VoiceAgent` and the generic `TranscriptSource`
 * contract.
 *
 * `VoiceAgent` pushes finalized lines through its `onNewTranscriptEntry`
 * callback, which is wired once at agent construction time. The transcript
 * pipeline, by contrast, pulls from a `TranscriptSource` whose `start(emit)`
 * is invoked by `useTranscriptStream` — potentially after the agent already
 * exists. This bridge decouples the two: the agent's callback calls `push`,
 * and the source subscribes to the bridge inside `start`. It is a small
 * fan-out so the same agent can feed multiple consumers if needed.
 */
export type VoiceAgentTranscriptBridge = {
	/** Forward a new transcript entry from the VoiceAgent to all subscribers. */
	push(entry: SparTranscriptEntry): void;
	/** Subscribe to entries; returns an unsubscribe function. */
	subscribe(cb: (entry: SparTranscriptEntry) => void): () => void;
};

/**
 * Create a fan-out bridge. Wire `bridge.push` into the agent via
 * `new VoiceAgent({ onNewTranscriptEntry: bridge.push, ... })`.
 */
export function createVoiceAgentTranscriptBridge(): VoiceAgentTranscriptBridge {
	const listeners = new Set<(entry: SparTranscriptEntry) => void>();
	return {
		push(entry) {
			listeners.forEach(cb => cb(entry));
		},
		subscribe(cb) {
			listeners.add(cb);
			return () => listeners.delete(cb);
		},
	};
}

/**
 * Map the Spar VoiceAgent's `speaker` string onto our neutral speaker bucket.
 *
 * The VoiceAgent labels the AI roleplay party `'Prospect'` and the human
 * salesperson `'Sales Rep'` (see `spar/VoiceAgent.ts` — the ElevenLabs
 * `onMessage` path uses these exact strings). The AI prospect plays the lead
 * being sold to, so it maps to `'lead'`; the human maps to `'rep'`. Anything
 * else is `'other'`.
 */
function mapSpeaker(speaker: string): TranscriptSpeaker {
	if (speaker === 'Prospect') return 'lead';
	if (speaker === 'Sales Rep') return 'rep';
	return 'other';
}

/**
 * Human-readable speaker label, mirroring `twilioSource`'s `speakerLabelFor`
 * so the UI renders consistent names across sources.
 */
function speakerLabelFor(speaker: TranscriptSpeaker): string | undefined {
	if (speaker === 'lead') return 'Lead';
	if (speaker === 'rep') return 'Sales Rep';
	return undefined;
}

/**
 * Convert a Spar transcript entry into a pipeline `TranscriptUtterance`. The
 * VoiceAgent only ever emits finalized lines, so every utterance is `isFinal`.
 * `partialKey` is the speaker string — there are no partials to group, but the
 * key keeps the contract satisfied and groups by speaker track for consistency.
 */
function mapToUtterance(entry: SparTranscriptEntry): TranscriptUtterance {
	const speaker = mapSpeaker(entry.speaker);
	return {
		sourceId: 'voice-agent',
		partialKey: entry.speaker,
		speaker,
		speakerLabel: speakerLabelFor(speaker),
		text: entry.message,
		isFinal: true,
		timestamp: entry.timestamp,
	};
}

/**
 * VoiceAgent implementation of the `TranscriptSource` contract. Subscribes to
 * the bridge on `start` and emits one finalized `TranscriptUtterance` per Spar
 * entry. The `context` is unused — Spar entries already carry their speaker.
 */
export function createVoiceAgentSource(bridge: VoiceAgentTranscriptBridge): TranscriptSource {
	return {
		id: 'voice-agent',
		start(emit) {
			return bridge.subscribe(entry => emit(mapToUtterance(entry)));
		},
	};
}
